import React from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  ShoppingBag,
  User,
  CreditCard,
  Truck,
  RotateCcw,
  ShieldCheck,
  AlertCircle,
  Scale,
  Mail,
  Phone,
  Globe,
  CheckCircle2
} from 'lucide-react';

const TermsOfUse = () => {
  const sections = [
    {
      num: '1',
      title: 'Acceptance of Terms',
      desc: 'By using our website, you agree to these Terms of Use and our Privacy Policy. If you do not agree, please do not use our website or services.',
      icon: FileText
    },
    {
      num: '2',
      title: 'Use of Our Website',
      desc: 'You agree to use our website for lawful purposes only and in a way that does not infringe the rights of others or restrict their use.',
      icon: ShoppingBag
    },
    {
      num: '3',
      title: 'Accounts',
      desc: 'You may be required to create an account to access certain features. You are responsible for maintaining the confidentiality of your account information.',
      icon: User
    },
    {
      num: '4',
      title: 'Orders & Payments',
      desc: 'All orders are subject to availability and acceptance. Prices are listed in GBP (£) / USD and may change without notice. We reserve the right to refuse or cancel any order.',
      icon: CreditCard
    },
    {
      num: '5',
      title: 'Shipping & Delivery',
      desc: 'Delivery times are estimates and may vary. We are not responsible for delays caused by carriers or customs.',
      icon: Truck
    },
    {
      num: '6',
      title: 'Returns & Refunds',
      desc: 'Our return and refund policy is outlined in our Returns & Refunds page, which is incorporated into these Terms by reference.',
      icon: RotateCcw
    },
    {
      num: '7',
      title: 'Intellectual Property',
      desc: 'All content on this site, including text, images, logos and graphics, is owned by or licensed to InfraEdge and is protected by copyright laws.',
      icon: ShieldCheck
    },
    {
      num: '8',
      title: 'Limitation of Liability',
      desc: 'To the fullest extent permitted by law, InfraEdge is not liable for any indirect, incidental, or consequential damages arising from your use of our website or products.',
      icon: AlertCircle
    }
  ];

  return (
    <div style={{ background: '#fafbfc', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#0f172a', padding: '40px 24px 80px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '1140px', margin: '0 auto' }}>
        
        {/* Breadcrumbs */}
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, marginBottom: '24px' }}>
          <Link to="/" style={{ color: '#64748b', textDecoration: 'none' }}>Home</Link>
          <span style={{ margin: '0 8px' }}>›</span>
          <span style={{ color: '#0f172a', fontWeight: 700 }}>Terms of Use</span>
        </div>

        {/* TOP HERO HEADER WITH DOCUMENT GRAPHIC */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px', alignItems: 'center', marginBottom: '48px' }}>
          <div>
            <h1 style={{ fontSize: '44px', fontWeight: 900, color: '#0f172a', lineHeight: 1.1, marginBottom: '16px', letterSpacing: '-0.02em' }}>
              Terms of Use
            </h1>
            <div style={{ width: '40px', height: '3px', background: '#dc2626', borderRadius: '2px', marginBottom: '20px' }} />
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#64748b', marginBottom: '16px' }}>
              Last updated: May 14, 2025
            </div>
            <p style={{ fontSize: '14.5px', color: '#475569', lineHeight: 1.7, maxWidth: '520px', margin: 0 }}>
              Welcome to InfraEdge. By accessing or using our website and services, you agree to be bound by these Terms of Use. Please read them carefully.
            </p>
          </div>

          {/* Right Vector Document & Checkmark Emblem Graphic */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
            <div style={{ position: 'relative', width: '280px', height: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {/* Outer Orbit Dashed Ring */}
              <div style={{ position: 'absolute', width: '260px', height: '260px', border: '2px dashed #fecaca', borderRadius: '50%', pointerEvents: 'none' }} />
              
              {/* Center Document Card */}
              <div style={{ width: '130px', height: '160px', background: '#ffffff', border: '1.5px solid #fecaca', borderRadius: '16px', padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '8px', boxShadow: '0 12px 30px rgba(220,38,38,0.08)', zIndex: 1, position: 'relative' }}>
                <div style={{ width: '60%', height: '4px', background: '#dc2626', borderRadius: '2px' }} />
                <div style={{ width: '90%', height: '4px', background: '#fca5a5', borderRadius: '2px' }} />
                <div style={{ width: '80%', height: '4px', background: '#fca5a5', borderRadius: '2px' }} />
                <div style={{ width: '100%', height: '4px', background: '#fee2e2', borderRadius: '2px', marginTop: '6px' }} />
                <div style={{ width: '70%', height: '4px', background: '#fee2e2', borderRadius: '2px' }} />

                {/* Red Check Circle Badge */}
                <div style={{ position: 'absolute', bottom: '-12px', right: '-12px', width: '40px', height: '40px', borderRadius: '50%', background: '#dc2626', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 16px rgba(220,38,38,0.3)', border: '3px solid #ffffff' }}>
                  <CheckCircle2 size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 8 TERMS SECTIONS GRID */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px', marginBottom: '48px' }}>
          {sections.map((sec) => {
            const Icon = sec.icon;
            return (
              <div key={sec.num} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '32px 24px', boxShadow: '0 4px 16px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                  <Icon size={22} />
                </div>
                <h3 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', marginBottom: '12px', lineHeight: 1.3 }}>
                  {sec.num}. {sec.title}
                </h3>
                <p style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.6, margin: 0 }}>
                  {sec.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* BOTTOM GOVERNING LAW & QUESTIONS BANNER */}
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '20px', padding: '28px 36px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '24px' }}>
          
          {/* Left Governing Law */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', maxWidth: '540px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#ffffff', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
              <Scale size={22} />
            </div>
            <div>
              <h4 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', margin: '0 0 6px 0' }}>Governing Law</h4>
              <p style={{ fontSize: '12.5px', color: '#475569', margin: 0, lineHeight: 1.6, fontWeight: 500 }}>
                These Terms are governed by and construed in accordance with the laws of the United Kingdom and the State of Delaware, USA, without regard to its conflict of law principles.
              </p>
            </div>
          </div>

          {/* Right Contact Details */}
          <div>
            <h4 style={{ fontSize: '15px', fontWeight: 900, color: '#0f172a', margin: '0 0 10px 0' }}>Questions?</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <a href="mailto:Info@infraedgeredlight.co.uk" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#dc2626', fontSize: '13px', fontWeight: 700, textDecoration: 'none' }}>
                <Mail size={15} />
                <span>Contact us at Info@infraedgeredlight.co.uk</span>
              </a>
              <a href="tel:01214556529" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '13px', fontWeight: 700, textDecoration: 'none' }}>
                <Phone size={15} color="#dc2626" />
                <span>0121 455 6529</span>
              </a>
              <a href="https://activeacademy.co.uk/red-light-infrared-therapy" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '13px', fontWeight: 700, textDecoration: 'none' }}>
                <Globe size={15} color="#dc2626" />
                <span>www.activeacademy.co.uk</span>
              </a>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default TermsOfUse;
